import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatCurrency, monthLabel } from "@/lib/format";
import {
  Wallet,
  CreditCard,
  Banknote,
  PiggyBank,
  TrendingUp,
  Briefcase,
  Activity,
} from "lucide-react";

// Map of account type -> default icon, used as a visual fallback.
const ICON_BY_TYPE = {
  cash: Banknote,
  debit: Wallet,
  credit: CreditCard,
  savings: PiggyBank,
  investment: TrendingUp,
  income: Briefcase,
};

const TYPE_LABEL = {
  cash: "Efectivo",
  debit: "Débito",
  credit: "Crédito",
  savings: "Ahorros",
  investment: "Inversión",
  income: "Ingresos",
};

function AccountRow({ item, totalExpense, currency }) {
  const Icon = ICON_BY_TYPE[item.type] || Wallet;
  const color = item.color || "#3B82F6";
  const pct = Math.min(100, Math.max(0, Number(item.expense_share_pct || 0)));
  const hasActivity = item.tx_count > 0;

  return (
    <div
      className="py-4 first:pt-0 last:pb-0 border-b border-[#1F2937] last:border-b-0"
      data-testid={`account-health-row-${item.account_id}`}
    >
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
            style={{ backgroundColor: `${color}22`, color }}
          >
            <Icon className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <p
                className="font-display text-base font-semibold text-[#F1F5F9] truncate"
                data-testid={`account-health-name-${item.account_id}`}
              >
                {item.name}
              </p>
              {item.is_default && (
                <span className="text-[10px] uppercase tracking-[0.18em] text-[#94A3B8] px-1.5 py-0.5 rounded bg-[#1F2937]">
                  Por defecto
                </span>
              )}
            </div>
            <p className="text-xs text-[#94A3B8] mt-0.5">
              {TYPE_LABEL[item.type] || item.type} ·{" "}
              <span data-testid={`account-health-txcount-${item.account_id}`}>
                {item.tx_count} {item.tx_count === 1 ? "movimiento" : "movimientos"}
              </span>
            </p>
          </div>
        </div>
        <div className="text-right shrink-0">
          <p
            className="font-numeric font-semibold text-base text-[#F1F5F9]"
            data-testid={`account-health-spent-${item.account_id}`}
          >
            {formatCurrency(item.expense || 0, currency)}
          </p>
          <p className="text-xs text-[#94A3B8] font-numeric">
            {totalExpense > 0 ? `${pct.toFixed(1)}%` : "—"}
          </p>
        </div>
      </div>
      <div className="h-1.5 rounded-full bg-[#0F172A] overflow-hidden">
        <div
          className="h-full rounded-full transition-all"
          style={{
            width: hasActivity && totalExpense > 0 ? `${pct}%` : "0%",
            backgroundColor: color,
          }}
        />
      </div>
    </div>
  );
}

export default function AccountHealthCard({ month, year, currency = "MXN" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get(`/accounts/health?month=${month}&year=${year}`)
      .then((r) => {
        if (!cancelled) setData(r.data);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [month, year]);

  const accounts = data?.accounts || [];
  const totalExpense = data?.total_expense || 0;
  const totalIncome = data?.total_income || 0;
  const visibleAccounts = accounts.filter((a) => a.tx_count > 0 || a.is_default);
  const noActivity = totalExpense === 0 && totalIncome === 0;

  return (
    <div
      className="clay-card p-6 fade-up"
      data-testid="account-health-card"
    >
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full flex items-center justify-center bg-[#3B82F622] text-[#3B82F6]">
            <Activity className="w-5 h-5" />
          </div>
          <div>
            <h2 className="font-display text-xl font-semibold text-[#F1F5F9]">
              Salud por cuenta
            </h2>
            <p className="text-sm text-[#94A3B8]">
              Gastos de {monthLabel(month, year)} por origen del dinero
            </p>
          </div>
        </div>
        <div className="text-right hidden sm:block">
          <p className="text-xs uppercase tracking-[0.18em] text-[#94A3B8]">
            Total del mes
          </p>
          <p
            className="font-numeric font-semibold text-lg text-[#F1F5F9]"
            data-testid="account-health-total-expense"
          >
            {formatCurrency(totalExpense, currency)}
          </p>
        </div>
      </div>

      {loading ? (
        <div className="py-10 text-center text-sm text-[#94A3B8]">Cargando…</div>
      ) : accounts.length === 0 ? (
        <div className="py-10 text-center text-sm text-[#94A3B8]" data-testid="account-health-empty">
          Aún no tienes cuentas registradas.
        </div>
      ) : noActivity ? (
        <div className="py-8 text-center" data-testid="account-health-empty-month">
          <p className="text-sm text-[#94A3B8] mb-1">
            Sin movimientos en {monthLabel(month, year)}.
          </p>
          <p className="text-xs text-[#64748B]">
            Cuando registres un gasto, verás qué cuenta lo financió.
          </p>
        </div>
      ) : (
        <div>
          {visibleAccounts.map((item) => (
            <AccountRow
              key={item.account_id}
              item={item}
              totalExpense={totalExpense}
              currency={currency}
            />
          ))}
        </div>
      )}
    </div>
  );
}
